import { Brand } from '@/domainLogic/index.ts';
import { CONTENT_HUB_BASE_URL } from '@/app/common/contentHubAssets.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { HEADER_HEIGHT } from '@/app/common/constants.ts';

type BrandConfig = { bg: string; logoSrc?: string; logoAlt?: string; label?: string };

export const STATIC_BRAND_CONFIG: Partial<Record<string, BrandConfig>> = {
  [Brand.LASTMINUTE]: {
    bg: '#f2007d',
    label: 'lastminute.com',
  },
  [Brand.RUMBO]: {
    bg: '#f2007d',
    logoSrc: `${CONTENT_HUB_BASE_URL}/v1/DAM/Artwork/Other/Logos-by-size/Rumbo/LOGO-Rumbo-by-lastminute-com-white-L_211`,
    logoAlt: 'rumbo by lastminute.com',
  },
  [Brand.VOLAGRATIS]: {
    bg: '#f2007d',
    logoSrc: `${CONTENT_HUB_BASE_URL}/v1/DAM/Artwork/Other/Logos-by-size/Volagratis/LOGO-Volagratis-by-lastminute-com-white-L_251`,
    logoAlt: 'Volagratis by lastminute.com',
  },
  [Brand.BRAVOFLY]: {
    bg: '#f2007d',
    logoSrc: `${CONTENT_HUB_BASE_URL}/v1/DAM/Artwork/Other/Logos-by-size/Bravofly/LOGO-Bravofly-by-lastminute-com-white-L_233`,
    logoAlt: 'bravofly by lastminute.com',
  },
  [Brand.HOLIDAYPIRATES]: {
    bg: '#ffffff',
    logoSrc: 'https://media.holidaypirates.group/icons/v2/logos/holidaypirates_purple.svg',
    logoAlt: 'HolidayPirates',
  },
  [Brand.BOOKING]: {
    bg: '#003b95',
    logoSrc: `${CONTENT_HUB_BASE_URL}/v1/DAM/Logos%20%2B%20fonts/Bookingcom/Booking.com_lastminute_simple-logo`,
    logoAlt: 'Booking.com powered by lastminute.com',
  },
  [Brand.HOLIDAYGURU]: {
    bg: '#ffffff',
    label: 'HolidayGuru',
  },
  [Brand.THB]: {
    bg: '#0d224c',
    logoSrc: `${CONTENT_HUB_BASE_URL}/v1/DAM/Artwork/White%20labels/THB-Hotels/thbhotels_lm`,
    logoAlt: 'THB hotels powered by lastminute.com',
  },
  [Brand.WEGDE]: {
    bg: '#ff9700',
    logoSrc: `${CONTENT_HUB_BASE_URL}/v1/DAM/Logos%20%2B%20fonts/SVG/logo_weg-de_negative_web_header`,
    logoAlt: 'weg.de',
  },
};

/**
 * Presentational component — accepts brand string directly.
 * Used both by StaticHeader (which reads brand from context) and by the debug page.
 */
export function StaticHeaderView({ brand }: { brand: string }) {
  const config = STATIC_BRAND_CONFIG[brand];
  const height = HEADER_HEIGHT(brand as Brand, false);
  const bg = config?.bg ?? '#1f2937';

  return (
    <header
      style={{
        height,
        display: 'flex',
        alignItems: 'center',
        padding: '0 20px',
        backgroundColor: bg,
        boxSizing: 'border-box',
      }}
      data-testid="static-header"
    >
      {config?.logoSrc ? (
        <img
          src={config.logoSrc}
          alt={config.logoAlt ?? brand}
          height={40}
          style={{ width: 'auto', display: 'block' }}
        />
      ) : (
        <span
          style={{
            color: '#fff',
            fontFamily: "'Open Sans', sans-serif",
            fontWeight: 700,
            fontSize: 22,
            letterSpacing: '-0.01em',
          }}
        >
          {config?.label ?? brand}
        </span>
      )}
    </header>
  );
}

/**
 * Minimal static header — last-resort fallback rendered when the Content Hub header
 * fails to fetch AND the LKG cache is cold, or on a React render error caught by
 * the ErrorBoundary. Shows the brand mark only; no nav, no interactivity.
 */
const StaticHeader = () => {
  const { localization } = usePageWrapperData();
  return <StaticHeaderView brand={localization.brand} />;
};

export default StaticHeader;
